import React, { useState } from "react";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import html2canvas from "html2canvas";

export const AnalyticsPdfExport = ({ optimizationResults, userData }) => {
  const [exporting, setExporting] = useState(false);

  const exportToPdf = async () => {
    setExporting(true);
    
    try {
      console.log("Starting PDF export...");
      
      // Create new jsPDF instance with error handling
      let pdf;
      try {
        pdf = new jsPDF("p", "mm", "a4");
        console.log("PDF instance created successfully");
      } catch (err) {
        console.error("Error creating PDF instance:", err);
        throw new Error("Failed to initialize PDF document");
      }
      
      const pageWidth = pdf.internal.pageSize.getWidth();
      
      // Add header with title
      pdf.setFontSize(22);
      pdf.setTextColor(44, 62, 80);
      pdf.text("Fleet Optimization Report", pageWidth / 2, 20, { align: "center" });
      
      // Check if data is available
      if (!optimizationResults?.data) {
        console.error("Optimization results data is missing:", optimizationResults);
        throw new Error("No optimization data available to export");
      }
      
      pdf.setFontSize(12);
      pdf.text(`Generated for: ${userData?.company_name || userData?.username || 'Your Company'}`, pageWidth / 2, 30, { align: "center" });
      pdf.text(`Date: ${new Date().toLocaleDateString()}`, pageWidth / 2, 36, { align: "center" });
      
      // Case information
      pdf.setFontSize(16);
      pdf.text("Case Information", 14, 50);
      pdf.setFontSize(12);
      pdf.text(`Case Name: ${optimizationResults?.case_name || 'Current Optimization'}`, 14, 60);

      // ----- FINANCIAL HIGHLIGHTS SECTION -----
      pdf.setFontSize(16);
      pdf.text("Financial Highlights", 14, 75);
      
      // Access TCO and other financial metrics
      const tcoAnalysis = optimizationResults?.data?.finance?.tco_analysis || {};
      const financialMetrics = tcoAnalysis?.financial_metrics || {};
      
      const totalCostOptimized = financialMetrics?.total_cost_of_ownership?.optimized_fleet || 0;
      const totalCostDiesel = financialMetrics?.total_cost_of_ownership?.pure_diesel_fleet || 0;
      const savings = financialMetrics?.total_cost_of_ownership?.savings || 0;
      const perMileSavings = financialMetrics?.tco_per_mile?.savings || 0;
      const currentPerMile = financialMetrics?.tco_per_mile?.pure_diesel_fleet || 0;
      const optimizedPerMile = financialMetrics?.tco_per_mile?.optimized_fleet || 0;

      const paybackPeriod = financialMetrics?.payback_period || 0;
      
      // Financial highlights with key metrics
      const financialHighlights = [
        ["Total 10-Year TCO (Optimized Fleet)", `$${Math.round(totalCostOptimized).toLocaleString() || '0'}`],
        ["Total 10-Year TCO (Pure Diesel)", `$${Math.round(totalCostDiesel).toLocaleString() || '0'}`],
        ["Total Cost Savings (10 years)", `$${Math.round(savings).toLocaleString() || '0'}`],
        ["Savings Percentage", `${((savings / totalCostDiesel) * 100).toFixed(1)}%`],
        ["Optimized Cost per Mile", `$${optimizedPerMile.toLocaleString(undefined, { minimumFractionDigits: 3, maximumFractionDigits: 3 }) || '0.000'}`],
        ["Current Cost per Mile", `$${currentPerMile.toLocaleString(undefined, { minimumFractionDigits: 3, maximumFractionDigits: 3 }) || '0.000'}`],
        ["Cost per Mile Reduction", `$${perMileSavings.toLocaleString(undefined, { minimumFractionDigits: 3, maximumFractionDigits: 3 }) || '0.000'}`],
        ["Payback Period", `${paybackPeriod.toFixed(1)} years`],
      ];
      
      console.log("Adding financial highlights table");
      try {
        autoTable(pdf, {
          startY: 80,
          head: [["Metric", "Value"]],
          body: financialHighlights,
          theme: 'striped',
          headStyles: { fillColor: [44, 62, 80] },
        });
      } catch (err) {
        console.error("Error creating financial highlights table:", err);
      }
      
      // ----- UPFRONT COSTS SECTION -----
      pdf.setFontSize(16);
      pdf.text("Upfront Costs Breakdown", 14, pdf.lastAutoTable.finalY + 15);
      
      // Get upfront costs with same structure as AnalyticsFinancials
      const dieselUpfrontCosts = optimizationResults?.data?.finance?.cost_overview?.upfront_costs?.diesel || {
        vehicle_cost: 0
      };
      
      const electricUpfrontCosts = optimizationResults?.data?.finance?.cost_overview?.upfront_costs?.electric || {
        vehicle_cost: 0,
        depot_construction: 0,
        charger_installation: 0,
        power_interconnection: 0,
        solar_installation: 0,
        battery_installation: 0
      };
      
      const upfrontCostsBreakdown = [
        ["Diesel Vehicles", `$${Math.round(dieselUpfrontCosts?.vehicle_cost).toLocaleString() || '0'}`],
        ["Electric Vehicles", `$${Math.round(electricUpfrontCosts?.vehicle_cost).toLocaleString() || '0'}`],
        ["Depot Construction", `$${Math.round(electricUpfrontCosts?.depot_construction).toLocaleString() || '0'}`],
        ["Charger Installation", `$${Math.round(electricUpfrontCosts?.charger_installation).toLocaleString() || '0'}`],
        ["Power Interconnection", `$${Math.round(electricUpfrontCosts?.power_interconnection).toLocaleString() || '0'}`],
        ["Solar Installation", `$${Math.round(electricUpfrontCosts?.solar_installation).toLocaleString() || '0'}`],
        ["Battery Storage Installation", `$${Math.round(electricUpfrontCosts?.battery_installation).toLocaleString() || '0'}`],
        ["Total Upfront Investment", `$${Math.round(optimizationResults?.data?.finance?.cost_overview?.upfront_costs?.total).toLocaleString() || '0'}`],
      ];
      
      try {
        autoTable(pdf, {
          startY: pdf.lastAutoTable.finalY + 20,
          head: [["Upfront Cost Category", "Amount"]],
          body: upfrontCostsBreakdown,
          theme: 'striped',
          headStyles: { fillColor: [44, 62, 80] },
        });
      } catch (err) {
        console.error("Error creating upfront costs table:", err);
      }
      
      // // ----- ANNUAL OPERATIONAL COSTS SECTION -----
      // pdf.setFontSize(16);
      // pdf.text("Annual Operational Costs", 14, pdf.lastAutoTable.finalY + 15);
      
      // // Safely access annual operational costs - use same structure as AnalyticsFinancials
      // const dieselAnnualCosts = optimizationResults?.data?.finance?.cost_overview?.annual_costs?.diesel || {
      //   vehicle_cost: 0,
      //   energy_cost: 0,
      //   insurance_cost: 0,
      //   maintenance_cost: 0,
      //   non_ze_penalties: 0
      // };
      
      // const electricAnnualCosts = optimizationResults?.data?.finance?.cost_overview?.annual_costs?.electric || {
      //   vehicle_cost: 0,
      //   depot_energy: 0,
      //   depot_demand: 0,
      //   public_charging_energy: 0,
      //   maintenance_cost: 0,
      //   insurance_cost: 0,
      //   energy_sold_revenue: 0
      // };
      
      // const annualCostsBreakdown = [
      //   ["Electric Vehicle Cost", `$${Math.round(electricAnnualCosts?.vehicle_cost).toLocaleString() || '0'}`],
      //   ["Electric Energy (Depot)", `$${Math.round(electricAnnualCosts?.depot_energy).toLocaleString() || '0'}`],
      //   ["Electric Demand Charges", `$${Math.round(electricAnnualCosts?.depot_demand).toLocaleString() || '0'}`],
      //   ["Public Charging Energy", `$${Math.round(electricAnnualCosts?.public_charging_energy).toLocaleString() || '0'}`],
      //   ["Electric Maintenance", `$${Math.round(electricAnnualCosts?.maintenance_cost).toLocaleString() || '0'}`],
      //   ["Electric Insurance", `$${Math.round(electricAnnualCosts?.insurance_cost).toLocaleString() || '0'}`],
      //   ["Energy Sold Revenue", `$${Math.round(electricAnnualCosts?.energy_sold_revenue || 0).toLocaleString()}`],
      //   ["Diesel Vehicle Cost", `$${Math.round(dieselAnnualCosts?.vehicle_cost).toLocaleString() || '0'}`],
      //   ["Diesel Fuel", `$${Math.round(dieselAnnualCosts?.energy_cost).toLocaleString() || '0'}`],
      //   ["Diesel Maintenance", `$${Math.round(dieselAnnualCosts?.maintenance_cost).toLocaleString() || '0'}`],
      //   ["Diesel Insurance", `$${Math.round(dieselAnnualCosts?.insurance_cost).toLocaleString() || '0'}`],
      //   ["Non-ZE Penalties", `$${Math.round(dieselAnnualCosts?.non_ze_penalties).toLocaleString() || '0'}`],
      //   ["Total Annual Cost", `$${Math.round(optimizationResults?.data?.finance?.cost_overview?.annual_costs?.operation).toLocaleString() || '0'}`],
      // ];
      
      // try {
      //   autoTable(pdf, {
      //     startY: pdf.lastAutoTable.finalY + 20,
      //     head: [["Operational Cost Category", "Annual Amount"]],
      //     body: annualCostsBreakdown,
      //     theme: 'striped',
      //     headStyles: { fillColor: [44, 62, 80] },
      //   });
      // } catch (err) {
      //   console.error("Error creating annual costs table:", err);
      // }

      // // ----- COST COMPARISON WITH DIESEL FLEET -----
      // pdf.setFontSize(16);
      // pdf.text("Annual Cost Comparison with Pure Diesel Fleet", 14, pdf.lastAutoTable.finalY + 15);
      
      // // Calculate electric energy costs total (same as in AnalyticsFinancials)
      // const electricEnergyTotal = (
      //   (electricAnnualCosts?.depot_energy || 0) +
      //   (electricAnnualCosts?.depot_demand || 0) +
      //   (electricAnnualCosts?.public_charging_energy || 0)
      // );
      
      // const annualComparisonTable = [
      //   ["Category", "Optimized Fleet", "Pure Diesel Fleet", "Difference"],
      //   ["Energy/Fuel Costs", 
      //     `$${Math.round(electricEnergyTotal + dieselAnnualCosts?.energy_cost || 0).toLocaleString()}`, 
      //     `$${Math.round(dieselAnnualCosts?.energy_cost).toLocaleString() || '0'}`,
      //     `$${Math.round((dieselAnnualCosts?.energy_cost || 0) - (electricEnergyTotal + dieselAnnualCosts?.energy_cost || 0)).toLocaleString()}`
      //   ],
      //   ["Vehicle Costs", 
      //     `$${Math.round(electricAnnualCosts?.vehicle_cost + dieselAnnualCosts?.vehicle_cost || 0).toLocaleString()}`, 
      //     `$${Math.round(dieselAnnualCosts?.vehicle_cost).toLocaleString() || '0'}`,
      //     `$${Math.round((dieselAnnualCosts?.vehicle_cost || 0) - (electricAnnualCosts?.vehicle_cost + dieselAnnualCosts?.vehicle_cost || 0)).toLocaleString()}`
      //   ],
      //   ["Maintenance", 
      //     `$${Math.round(electricAnnualCosts?.maintenance_cost + dieselAnnualCosts?.maintenance_cost || 0).toLocaleString()}`, 
      //     `$${Math.round(dieselAnnualCosts?.maintenance_cost).toLocaleString() || '0'}`,
      //     `$${Math.round((dieselAnnualCosts?.maintenance_cost || 0) - (electricAnnualCosts?.maintenance_cost + dieselAnnualCosts?.maintenance_cost || 0)).toLocaleString()}`
      //   ],
      //   ["Insurance", 
      //     `$${Math.round(electricAnnualCosts?.insurance_cost + dieselAnnualCosts?.insurance_cost || 0).toLocaleString()}`, 
      //     `$${Math.round(dieselAnnualCosts?.insurance_cost).toLocaleString() || '0'}`,
      //     `$${Math.round((dieselAnnualCosts?.insurance_cost || 0) - (electricAnnualCosts?.insurance_cost + dieselAnnualCosts?.insurance_cost || 0)).toLocaleString()}`
      //   ],
      //   ["Non-ZE Penalties", 
      //     `$${Math.round(dieselAnnualCosts?.non_ze_penalties).toLocaleString() || '0'}`, 
      //     `$${Math.round(dieselAnnualCosts?.non_ze_penalties).toLocaleString() || '0'}`,
      //     `$0`
      //   ],
      //   ["Energy Sold Revenue", 
      //     `-$${Math.round(electricAnnualCosts?.energy_sold_revenue || 0).toLocaleString()}`,
      //     "$0",
      //     `-$${Math.round(electricAnnualCosts?.energy_sold_revenue || 0).toLocaleString()}`
      //   ],
      //   ["Total Annual", 
      //     `$${Math.round(optimizationResults?.data?.finance?.cost_overview?.annual_costs?.operation).toLocaleString() || '0'}`, 
      //     `$${Math.round(dieselAnnualCosts?.total).toLocaleString() || '0'}`,
      //     `$${Math.round((dieselAnnualCosts?.total || 0) - (optimizationResults?.data?.finance?.cost_overview?.annual_costs?.operation || 0)).toLocaleString() || '0'}`
      //   ],
      // ];
      
      // try {
      //   autoTable(pdf, {
      //     startY: pdf.lastAutoTable.finalY + 20,
      //     body: annualComparisonTable,
      //     theme: 'striped',
      //     headStyles: { fillColor: [44, 62, 80] },
      //     columnStyles: {
      //       0: { fontStyle: 'bold' },
      //     },
      //   });
      // } catch (err) {
      //   console.error("Error creating annual comparison table:", err);
      // }
      
      // ----- CUMULATIVE COSTS OVER TIME -----
      if (tcoAnalysis?.cumulative_costs?.years && tcoAnalysis?.cumulative_costs?.years.length > 0) {
        pdf.setFontSize(16);
        pdf.text("Cumulative Cost Over Time", 14, pdf.lastAutoTable.finalY + 15);
        
        const years = tcoAnalysis.cumulative_costs.years;
        const optimizedCosts = tcoAnalysis.cumulative_costs.optimized_fleet;
        const dieselCosts = tcoAnalysis.cumulative_costs.pure_diesel_fleet;
        const differences = tcoAnalysis.cumulative_costs.difference;
        
        const cumulativeCostsData = years.map((year, index) => {
          const dieselCost = dieselCosts[index] || 0;
          const optimizedCost = optimizedCosts[index] || 0;
          const difference = dieselCost - optimizedCost;
          const savingsPercent = dieselCost > 0 ? ((difference / dieselCost) * 100).toFixed(1) + '%' : '0.0%';
          
          return [
            `Year ${year}`,
            `$${Math.round(optimizedCost).toLocaleString()}`,
            `$${Math.round(dieselCost).toLocaleString()}`,
            `$${Math.round(difference).toLocaleString()}`,
            savingsPercent
          ];
        });
        
        try {
          autoTable(pdf, {
            startY: pdf.lastAutoTable.finalY + 20,
            head: [["Year", "Optimized Fleet", "Pure Diesel Fleet", "Savings", "Savings %"]],
            body: cumulativeCostsData,
            theme: 'striped',
            headStyles: { fillColor: [44, 62, 80] },
          });
          
          // Add note explaining Year 0 values
          pdf.setFontSize(10);
          pdf.setTextColor(80, 80, 80);
          pdf.text("* Year 0 values represent upfront costs less the present value of residual values of the assets.", 14, pdf.lastAutoTable.finalY + 5);
          pdf.setTextColor(44, 62, 80); // Reset text color
        } catch (err) {
          console.error("Error creating cumulative costs table:", err);
        }
      }

      // Fleet Electrification summary
      pdf.setFontSize(16);
      pdf.text("Fleet Electrification Summary", 14, pdf.lastAutoTable.finalY + 15);
      
      // Safely handle routes and depot counts
      const routes = optimizationResults?.data?.operation?.routes || {};
      const depots = optimizationResults?.data?.operation?.depots || {};
      
      // Get route counts safely
      const totalRoutes = Object.keys(routes).length;
      const electricRoutes = Object.values(routes)
        .filter(route => route?.is_electric === true).length;
      
      // Get charger counts from depots safely
      const totalChargers = Object.values(depots).reduce((total, depot) => {
        return total + Object.values(depot?.chargers || {}).reduce((sum, num) => sum + num, 0);
      }, 0);
      
      // Calculate percentage with safety check
      const electrificationPercentage = totalRoutes > 0 ? ((electricRoutes / totalRoutes) * 100).toFixed(1) : '0.0';
      
      const electrificationSummary = [
        ["Total Routes", totalRoutes.toString()],
        ["Electrified Routes", electricRoutes.toString()],
        ["Percentage Electrified", `${electrificationPercentage}%`],
        ["Total Depots", Object.keys(depots).length.toString()],
        ["Total Chargers Required", totalChargers.toString()],
      ];
      
      console.log("Adding electrification summary table");
      try {
        autoTable(pdf, {
          startY: pdf.lastAutoTable.finalY + 20,
          head: [["Electrification Metric", "Value"]],
          body: electrificationSummary,
          theme: 'striped',
          headStyles: { fillColor: [44, 62, 80] },
        });
      } catch (err) {
        console.error("Error creating electrification summary table:", err);
      }
      
      // Save and download the PDF with explicit error handling
      console.log("Saving PDF...");
      try {
        const fileName = `fleet-optimization-${new Date().toISOString().split('T')[0]}.pdf`;
        pdf.save(fileName);
        console.log("PDF saved successfully:", fileName);
      } catch (err) {
        console.error("Error saving PDF:", err);
        // Alternative download approach
        try {
          const blob = pdf.output('blob');
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `fleet-optimization-${new Date().toISOString().split('T')[0]}.pdf`;
          link.click();
          URL.revokeObjectURL(url);
          console.log("PDF downloaded via alternative method");
        } catch (altErr) {
          console.error("Alternative download method failed:", altErr);
          throw new Error("Failed to download PDF");
        }
      }
    } catch (error) {
      console.error("Error in PDF export process:", error);
      alert(`Failed to generate PDF: ${error.message}`);
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="pdf-export-container">
      <button 
        className="export-pdf-button"
        onClick={exportToPdf}
        disabled={exporting}
      >
        <i className="fa fa-file-pdf-o"></i> {exporting ? 'Generating PDF...' : 'Export to PDF'}
      </button>
    </div>
  );
}; 